import React, { Fragment, useState, useRef } from "react"
import TeaserMessageCard from "../../teaser-card/teaser-message-card"
import Locations from "./locations"
import VideoCard from "./video-card"
import MerchantItem from "../merchant-item/merchant-item"
import OutsideClickTrigger from "../../../booking/components/common/OutsideClickTrigger"

export default function MerchantList({
  messageCards,
  videoCards,
  amountShown,
  page,
  merchants,
  numFound,
  noneFoundHeader,
  noneFoundText,
  loadMore,
  loadMoreLabel,
  activeTabLabel,
  locations,
  beforeSecurityLabel,
  afterSecurityLabel,
  selectLocations,
  selectedLocations,
  clearFilteringLabel,
  selectFilteringLabel,
  filters,
  clearFilters,
  currentAllMerchants,
  hideVideoCards,
  locationLabel,
  isKeyboardNavigating,
  shopListRef,
  hideLocations,
}) {
  const [locationsOpen, setLocationsOpen] = useState(false)

  const [areLocationsFocused, setAreLocationsFocused] = useState(false)

  const handleLocationFocus = () => {
    setAreLocationsFocused(isKeyboardNavigating && true)
  }

  const handleLocationBlur = () => {
    setAreLocationsFocused(false)
  }

  const getSpecialCardAtIndexPosition = (cards, index) => {
    if (cards && cards.length > 0) {
      return cards.filter((el) => el.position === index + 1)
    }
    return undefined
  }

  const dropDownRef = useRef(null)

  const handlePointerDown = (event) => {
    if (dropDownRef.current?.contains(event.target)) {
      // Prevent closing if clicking inside the dropdown
      return
    }
    setLocationsOpen((prev) => !prev)
  }

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault() // Prevents scrolling when space is pressed
      handlePointerDown(e) // Handle keyboard events (Enter or Space key)
    }
  }

  const handleBlur = (event) => {
    if (dropDownRef.current && !dropDownRef.current.contains(event.relatedTarget) && !event.currentTarget.contains(event.relatedTarget)) {
      setLocationsOpen(false)
    }
  }

  let displayAmount = amountShown * (page + 1)
  let cards = []

  if (merchants && merchants.length > 0) {
    for (let i = 0; i < displayAmount; i++) {
      const card = merchants[i]
      const messageCardsAtIndex = getSpecialCardAtIndexPosition(messageCards, i)
      if (messageCards?.length > 0) {
        cards.push(
          messageCardsAtIndex.map((messageCard, index) => {
            displayAmount-- // Adjusting to ensure the total count matches amountShown after adding message cards.
            return <TeaserMessageCard key={`messageCard${i}_${index}`} {...messageCard} />
          }),
        )
      }
      if (!hideVideoCards) {
        const videoCardsAtIndex = getSpecialCardAtIndexPosition(videoCards, i)
        if (videoCards?.length > 0) {
          cards.push(
            videoCardsAtIndex.map((videoCard, index) => {
              displayAmount -= 2 // Adjust by 2 because video card fills 2 tiles
              return <VideoCard key={`videoCard${i}_${index}`} {...videoCard} />
            }),
          )
        }
      }
      if (card) {
        cards.push(
          <MerchantItem
            key={`${card.name}${i}`}
            cssClass={"store-item"}
            link={card.merchantUrl}
            splashLabel={card.ribbonText}
            ribbonColor={card.ribbonColor}
            isRibbonValid={card.isRibbonValid}
            image={card.teaserImage}
            name={card.name}
            shopLocation={card.shopLocation}
            opening={card.opening}
            alwaysOpen={card.alwaysOpen}
            alwaysOpenLabel={card.alwaysOpenLabel}
          />,
        )
      }
      if (i === numFound - 1) {
        break
      }
    }
  }

  return (
    <React.Fragment>
      <div className="shop-list">
        <div className="w--wider">
          {numFound == 0 ? (
            <div id="search-results" aria-live="polite" role="status" className="none-found-container">
              <svg className={`svg-icon svg-icon--Store none-found-svg`}>
                <use xmlnsXlink="http://www.w3.org/1999/xlink" xlinkHref={`/dist/svg/symbols.svg#svg-Store`}>
                  &nbsp;
                </use>
              </svg>
              <h2 className="none-found-header">{noneFoundHeader || "Desværre"}</h2>
              <p className="none-found-text">{noneFoundText || "Din søgning gav ingen resultater"}</p>
            </div>
          ) : (
            <Fragment>
              <div className="rich-text shop-list__results">
                <p id="search-results" aria-live="polite" role="status" className="result-text">{`${numFound || 0} ${activeTabLabel}`}</p>
                {!hideLocations ? (
                  <OutsideClickTrigger
                    onOutsideClick={() => setLocationsOpen(false)}
                    modifier={`placering${areLocationsFocused && isKeyboardNavigating ? " focus-visible" : ""}`}
                  >
                    <>
                      <button
                        onPointerDown={handlePointerDown}
                        onKeyDown={(e) => handleKeyDown(e)}
                        onFocus={handleLocationFocus}
                        onBlur={handleLocationBlur}
                        className="locations-toggler"
                        aria-expanded={locationsOpen}
                      >
                        {locationLabel}
                        <svg className={`svg-icon svg-icon--arrowdownangle${locationsOpen ? " open" : ""}`}>
                          <use xmlnsXlink="http://www.w3.org/1999/xlink" xlinkHref="/dist/svg/symbols.svg#svg-arrowdownangle">
                            &nbsp;
                          </use>
                        </svg>
                      </button>
                      <Locations
                        dropDownRef={dropDownRef}
                        handleBlur={handleBlur}
                        locations={locations}
                        beforeSecurityLabel={beforeSecurityLabel}
                        afterSecurityLabel={afterSecurityLabel}
                        selectLocations={selectLocations}
                        selectedLocations={selectedLocations}
                        clearFilteringLabel={clearFilteringLabel}
                        selectFilteringLabel={selectFilteringLabel}
                        filters={filters}
                        clearFilters={clearFilters}
                        merchants={currentAllMerchants}
                        locationLabel={locationLabel}
                        isKeyboardNavigating={isKeyboardNavigating}
                        locationsOpen={locationsOpen}
                      />
                    </>
                  </OutsideClickTrigger>
                ) : (
                  ""
                )}
              </div>
              <div ref={shopListRef} className="shop-list__list grid">
                {cards}
              </div>
            </Fragment>
          )}
        </div>
      </div>
      {displayAmount < numFound && (
        <section className="deck deck--white deck--margin-bottom shop-list-progress">
          <div className="w--wider">
            <div className="show-more-button-container">
              <button className="show-more-button" onClick={loadMore}>
                {loadMoreLabel}
              </button>
            </div>
          </div>
        </section>
      )}
    </React.Fragment>
  )
}
